import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import { useFaxStatus } from '@/lib/serverMeta';
import FilledFormPreview from '@/components/FilledFormPreview';
import SuggestionHint from '@/components/SuggestionHint';
import { CodePicker } from '@/components/CodePicker';
import { GapChecklist, groupGaps } from '@/components/GapChecklist';
import {
  Sparkles, CheckCircle2, AlertTriangle, ExternalLink, Send, ListChecks,
  Keyboard, ChevronRight, RefreshCw, ScanLine,
} from 'lucide-react';
import { cn } from '@/lib/utils';

function ConfidenceChip({ ok, okLabel = 'AI matched', warnLabel = 'Check me' }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(152_68%_92%)] text-[hsl(158_64%_22%)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
      <CheckCircle2 className="h-3 w-3" /> {okLabel}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
      <AlertTriangle className="h-3 w-3" /> {warnLabel}
    </span>
  );
}

function CheckRow({ label, chipOk, children }) {
  return (
    <div className="py-2.5 border-b last:border-b-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
        <ConfidenceChip ok={chipOk} />
      </div>
      {children}
    </div>
  );
}

export default function NurseFastReview() {
  const faxStatus = useFaxStatus();
  const navigate = useNavigate();
  const { packetTypes } = useApp();
  const [queue, setQueue] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [idx, setIdx] = useState(0);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prepping, setPrepping] = useState(false);

  const load = useCallback(async () => {
    try {
      const [q, c] = await Promise.all([api.get('/nurse/fast-review/queue'), api.get('/catalog')]);
      setQueue(q.data.cases || []);
      setCatalog(c.data || []);
      setIdx(0);
    } catch (e) {
      toast.error(errText(e, 'Could not load fast review queue'));
      setQueue([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const current = queue && queue.length > 0 ? queue[Math.min(idx, queue.length - 1)] : null;

  // (Re-)initialize the editable draft whenever the visible case changes
  useEffect(() => {
    if (!current) { setDraft(null); return; }
    setDraft({
      procedure_id: current.procedure_id || '',
      procedure_name: current.procedure_name || '',
      icd10: current.icd10 || '',
      cpt: current.cpt || '',
      severity: current.severity || 'Moderate',
      packet_type_id: current.packet_type_id || 'fmla',
      leave_start: current.leave_start || '',
      leave_end: current.leave_end || '',
      delivery_method: current.delivery_method || 'Email',
      employer_email: current.employer_email || '',
      employer_fax: current.employer_fax || '',
      due_date: current.due_date || '',
      // employer-form answers typed here, keyed by question id — prefilled with
      // the AI's drafts (not the low-confidence ones), which stay drafts until saved
      answers: Object.fromEntries((current.employer_form_unanswered || [])
        .filter((q) => q.suggestion && q.suggestion.confidence !== 'low')
        .map((q) => [q.id, q.suggestion.answer])),
    });
  }, [current?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setD = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const onProcedureChange = (pid) => {
    const found = catalog.find((c) => c.id === pid);
    setDraft((d) => ({
      ...d,
      procedure_id: pid,
      procedure_name: found?.name || d.procedure_name,
      icd10: found?.icd10 || d.icd10,
      cpt: found ? (found.cpt || '') : d.cpt,
    }));
  };

  const conf = current?.auto_prep?.confidence || {};
  const flags = current?.auto_prep?.flags || [];
  const prepped = current?.auto_prep?.status === 'done';

  // §2 (2026-09-06): the server's completeness gate refuses a scanned employer
  // form with unanswered questions. This check used to omit that, so exactly the
  // cases with a scan bounced with a 409 and forced "Open full editor". The row
  // now carries the questions, and they are answered right here.
  const unanswered = current?.employer_form_unanswered || [];
  const answersDone = unanswered.every((q) => (draft?.answers?.[q.id] || '').trim());
  const setAnswer = (qid, v) => setDraft((d) => ({ ...d, answers: { ...(d.answers || {}), [qid]: v } }));
  const drafted = unanswered.filter((q) => q.suggestion && (draft?.answers?.[q.id] || '').trim());
  // A checklist's "None of these" / "Clear" (GapChecklist) saves straight away.
  const setGroup = async (picks) => {
    try {
      const r = await api.put(`/submissions/${current.id}/employer-form/answers`, { answers: picks });
      setDraft((d) => ({ ...d, answers: { ...(d.answers || {}), ...picks } }));
      setQueue((qs) => qs.map((c) => (c.id === current.id && Array.isArray(r?.unanswered)
        ? { ...c, employer_form_unanswered: r.unanswered } : c)));
    } catch (e) {
      toast.error(errText(e, 'Could not save the answers'));
    }
  };
  const renderGap = (q) => (
    <div key={q.id} className="space-y-1">
      <div className="text-sm">{q.question}</div>
      {q.answer_type === 'yes_no' ? (
        <Select value={draft.answers?.[q.id] || ''} onValueChange={(v) => setAnswer(q.id, v)}>
          <SelectTrigger className="h-9" data-testid={`fast-review-answer-${q.id}`}><SelectValue placeholder="Yes or no" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Yes">Yes</SelectItem>
            <SelectItem value="No">No</SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <Input
          className="h-9"
          type={q.answer_type === 'date' ? 'date' : 'text'}
          placeholder="Your answer"
          value={draft.answers?.[q.id] || ''}
          onChange={(e) => setAnswer(q.id, e.target.value)}
          data-testid={`fast-review-answer-${q.id}`}
        />
      )}
      <SuggestionHint q={q} value={draft.answers?.[q.id]} />
    </div>
  );
  const acceptSuggestions = async () => {
    try {
      const picks = Object.fromEntries(drafted.map((q) => [q.id, draft.answers[q.id]]));
      const r = await api.put(`/submissions/${current.id}/employer-form/answers`, { answers: picks });
      setQueue((qs) => qs.map((c) => (c.id === current.id && Array.isArray(r?.unanswered)
        ? { ...c, employer_form_unanswered: r.unanswered } : c)));
      toast.success(`Saved ${drafted.length} answer${drafted.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(errText(e, 'Could not save the answers'));
    }
  };

  // Mirrors the server's completeness gate, no stricter: an ICD-10 code is what
  // it needs, and a procedure outside the catalog is typed rather than picked.
  const deliveryOk = draft && (draft.delivery_method === 'Email' ? !!draft.employer_email.trim()
    : draft.delivery_method === 'Fax' ? !!(draft.employer_fax || '').trim() : true);
  const canApprove = !!(draft && draft.icd10.trim() && deliveryOk && answersDone);
  const blocker = !draft ? '' : !draft.icd10.trim() ? 'ICD-10 code required.'
    : (draft.delivery_method === 'Email' && !deliveryOk) ? 'Add the employer email (or switch delivery method).'
    : (draft.delivery_method === 'Fax' && !deliveryOk) ? 'Add the employer fax number (or switch delivery method).'
    : !answersDone ? `Answer the employer's ${unanswered.length} question${unanswered.length === 1 ? '' : 's'} below.` : '';

  const approve = useCallback(async () => {
    if (!current || !draft || busy) return;
    if (!canApprove) {
      toast.error(blocker || 'This case is not ready to send.');
      return;
    }
    setBusy(true);
    try {
      if (unanswered.length > 0) {
        await api.put(`/submissions/${current.id}/employer-form/answers`, { answers: draft.answers || {} });
      }
      await api.patch(`/submissions/${current.id}/clinical`, {
        nurse_clinical: {
          procedure_id: draft.procedure_id || null,
          procedure_name: draft.procedure_name,
          icd10: draft.icd10,
          cpt: draft.cpt,
          severity: draft.severity,
        },
        packet_type_id: draft.packet_type_id,
        delivery: {
          method: draft.delivery_method,
          employer_email: draft.employer_email,
          due_date: draft.due_date,
          // Only when faxing: an empty field here must not wipe a number on file.
          ...(draft.delivery_method === 'Fax' ? { employer_fax: draft.employer_fax.trim() } : {}),
        },
      });
      await api.post(`/submissions/${current.id}/send-to-doctor`, { note: 'Fast-reviewed by health admin (AI auto-prepped)' });
      const remaining = queue.filter((c) => c.id !== current.id);
      setQueue(remaining);
      setIdx((i) => Math.min(i, Math.max(remaining.length - 1, 0)));
      toast.success(`Sent to doctor — ${current.patient_name}. ${remaining.length} remaining.`);
    } catch (e) {
      toast.error(errText(e, 'Approve failed'));
    } finally { setBusy(false); }
  }, [current, draft, busy, canApprove, blocker, unanswered.length, queue]);

  const runPrep = async () => {
    if (!current || prepping) return;
    setPrepping(true);
    try {
      const r = await api.post(`/submissions/${current.id}/auto-prep`, {});
      toast.success(r.data.ok ? 'AI prep complete' : 'AI prep finished with warnings');
      // refresh just this queue (positions preserved by id)
      const q = await api.get('/nurse/fast-review/queue');
      const cases = q.data.cases || [];
      setQueue(cases);
      const newIdx = cases.findIndex((c) => c.id === current.id);
      if (newIdx >= 0) setIdx(newIdx);
    } catch (e) {
      toast.error(errText(e, 'AI prep failed'));
    } finally { setPrepping(false); }
  };

  // Keyboard: Enter approve, J/K nav, O full editor (ignored while typing)
  useEffect(() => {
    const onKey = (e) => {
      // Only when nothing is focused and no dialog is open, so Enter on a focused
      // control never accidentally sends the case to the doctor (the billable
      // event) — GAPS F19. Ignore auto-repeat.
      const ae = document.activeElement;
      if (ae && ae !== document.body) return;
      if (document.querySelector('[role="dialog"]')) return;
      if (e.repeat) return;
      if (e.key === 'Enter') { e.preventDefault(); approve(); }
      else if (e.key === 'j' || e.key === 'J') setIdx((i) => Math.min(i + 1, (queue?.length || 1) - 1));
      else if (e.key === 'k' || e.key === 'K') setIdx((i) => Math.max(i - 1, 0));
      else if ((e.key === 'o' || e.key === 'O') && current) navigate(`/nurse/submission/${current.id}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [approve, queue, current, navigate]);

  if (queue === null) {
    return (
      <div className="p-4 lg:p-6 max-w-6xl mx-auto space-y-4">
        <Skeleton className="h-9 w-56" />
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (queue.length === 0) {
    return (
      <div className="p-4 lg:p-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="h-5 w-5 text-primary" />
          <h1 className="text-base lg:text-lg font-semibold tracking-tight">Fast Review</h1>
        </div>
        <Card className="max-w-lg mx-auto mt-12" data-testid="fast-review-empty-state">
          <CardContent className="p-10 text-center space-y-3">
            <CheckCircle2 className="h-12 w-12 text-primary mx-auto" />
            <div className="text-lg font-semibold">Queue is clear</div>
            <p className="text-sm text-muted-foreground">New paid cases arrive here already AI-prepped and ready to check.</p>
            <Button variant="outline" onClick={() => navigate('/nurse')} data-testid="fast-review-back-to-queue">
              <ListChecks className="h-4 w-4 mr-1.5" /> Go to full queue
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Sparkles className="h-5 w-5 text-primary" />
        <h1 className="text-base lg:text-lg font-semibold tracking-tight">Fast Review</h1>
        <Badge variant="secondary" data-testid="fast-review-count">{queue.length} to check</Badge>
        <div className="flex-1" />
        <span className="hidden lg:inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Keyboard className="h-3.5 w-3.5" /> Enter = approve · J/K = next/prev · O = full editor
        </span>
        <Button variant="outline" size="sm" onClick={() => navigate('/nurse')} data-testid="fast-review-full-queue-link">
          <ListChecks className="h-4 w-4 mr-1.5" /> Full queue
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4 items-start">
        {/* ------- Checklist card ------- */}
        <div key={current.id} className="animate-in fade-in slide-in-from-right-4 duration-150">
            <Card className="rounded-2xl shadow-sm" data-testid="fast-review-case-card">
              <CardHeader className="pb-2">
                <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
                  <span data-testid="fast-review-patient-name">{current.patient_name || 'Unnamed patient'}</span>
                  {prepped ? (
                    <Badge variant="outline" className="text-[10px] border-primary/40 text-primary">
                      <Sparkles className="h-3 w-3 mr-1" /> AI-prepped
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">Not prepped</Badge>
                  )}
                  {current.addendum && (
                    <Badge variant="outline" className="text-[10px]"><ScanLine className="h-3 w-3 mr-1" /> Employer form</Badge>
                  )}
                  {(current.open_revision_count || 0) > 0 && (
                    <Badge className="text-[10px] bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] border border-[hsl(45_93%_84%)] hover:bg-[hsl(48_96%_88%)]" data-testid="fast-review-revision-badge">
                      <AlertTriangle className="h-3 w-3 mr-1" /> Change requested
                    </Badge>
                  )}
                  {current.overdue && <Badge variant="destructive" className="text-[10px]">Overdue</Badge>}
                </CardTitle>
                <div className="text-xs text-muted-foreground">
                  {current.employer_name || 'Employer —'} · {current.job_title || '—'} · DOB {current.dob || '—'}
                </div>
                <div className="text-sm text-foreground/90 mt-1 italic" data-testid="fast-review-reason">
                  “{current.reason || 'No reason given'}”
                </div>
              </CardHeader>
              <CardContent>
                {!prepped && (
                  <div className="mb-3 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 flex items-center justify-between gap-2">
                    <span className="text-sm">This case hasn't been AI-prepped yet.</span>
                    <Button size="sm" onClick={runPrep} disabled={prepping} data-testid="fast-review-run-prep-button">
                      <RefreshCw className={cn('h-4 w-4 mr-1.5', prepping && 'animate-spin')} /> {prepping ? 'Prepping…' : 'Run AI prep'}
                    </Button>
                  </div>
                )}

                {flags.length > 0 && (
                  <div className="mb-3 rounded-lg border border-[hsl(45_93%_84%)] bg-[hsl(48_96%_92%)] text-[hsl(32_92%_28%)] px-3 py-2 text-xs space-y-0.5" data-testid="fast-review-flags">
                    {flags.map((f, i) => (
                      <div key={i} className="flex items-start gap-1.5"><AlertTriangle className="h-3.5 w-3.5 mt-px shrink-0" /> {f}</div>
                    ))}
                  </div>
                )}

                {draft && (
                  <div>
                    <CheckRow label="Procedure" chipOk={prepped && conf.procedure === 'high' && !!draft.procedure_id}>
                      <Select value={draft.procedure_id || ''} onValueChange={onProcedureChange}>
                        <SelectTrigger className="h-9" data-testid="fast-review-procedure-select"><SelectValue placeholder="Select procedure" /></SelectTrigger>
                        <SelectContent>
                          {catalog.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.icd10}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {/* Not in the catalog? Type it — the server needs only the ICD-10. */}
                      <Input className="h-9 mt-1.5" placeholder="…or type the procedure" value={draft.procedure_name}
                        onChange={(e) => setDraft((d) => ({ ...d, procedure_name: e.target.value, procedure_id: '' }))}
                        data-testid="fast-review-procedure-name-input" />
                      {current.auto_prep?.rationale && (
                        <p className="text-xs text-muted-foreground mt-1" data-testid="fast-review-rationale">AI: {current.auto_prep.rationale}</p>
                      )}
                    </CheckRow>

                    <div className="grid grid-cols-2 gap-3">
                      <CheckRow label="Severity" chipOk={prepped && conf.severity === 'high'}>
                        <Select value={draft.severity} onValueChange={(v) => setD('severity', v)}>
                          <SelectTrigger className="h-9" data-testid="fast-review-severity-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Mild">Mild</SelectItem>
                            <SelectItem value="Moderate">Moderate</SelectItem>
                            <SelectItem value="Severe">Severe</SelectItem>
                          </SelectContent>
                        </Select>
                      </CheckRow>
                      <CheckRow label="ICD-10" chipOk={!!draft.icd10}>
                        <CodePicker kind="icd10" value={draft.icd10} onChange={(v) => setD('icd10', v)} testid="fast-review-icd10-input" />
                      </CheckRow>
                      <div className="py-2.5 border-b last:border-b-0">
                        <span className="block text-xs uppercase tracking-wide text-muted-foreground mb-1.5">CPT</span>
                        <CodePicker kind="procedure" value={draft.cpt} onChange={(v) => setD('cpt', v)} placeholder="e.g. 29881" testid="fast-review-cpt-input" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <CheckRow label="Packet" chipOk={prepped && conf.packet_type === 'high'}>
                        <Select value={draft.packet_type_id} onValueChange={(v) => setD('packet_type_id', v)}>
                          <SelectTrigger className="h-9" data-testid="fast-review-packet-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {(packetTypes || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </CheckRow>
                      <CheckRow label="Leave dates" chipOk={!!draft.leave_start && !!draft.leave_end && !flags.some((f) => f.includes('estimated'))}>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Input type="date" className="h-9 flex-1 min-w-[130px]" value={draft.leave_start} onChange={(e) => setD('leave_start', e.target.value)} data-testid="fast-review-leave-start" />
                          <span className="text-muted-foreground text-xs">→</span>
                          <Input type="date" className="h-9 flex-1 min-w-[130px]" value={draft.leave_end} onChange={(e) => setD('leave_end', e.target.value)} data-testid="fast-review-leave-end" />
                        </div>
                      </CheckRow>
                    </div>

                    <CheckRow label="Delivery" chipOk={!!deliveryOk}>
                      <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr_130px] gap-2">
                        <Select value={draft.delivery_method} onValueChange={(v) => setD('delivery_method', v)}>
                          <SelectTrigger className="h-9" data-testid="fast-review-delivery-method"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Email">Email</SelectItem>
                            {/* GAPS R6: don't offer a channel with no provider wired. */}
                            <SelectItem value="Fax" disabled={!faxStatus.enabled}>
                              {faxStatus.enabled ? 'Fax' : 'Fax (not available)'}
                            </SelectItem>
                            <SelectItem value="Patient-collected">Patient-collected</SelectItem>
                          </SelectContent>
                        </Select>
                        {draft.delivery_method === 'Fax' ? (
                          <Input className="h-9" type="tel" placeholder="employer HR fax number" value={draft.employer_fax} onChange={(e) => setD('employer_fax', e.target.value)} data-testid="fast-review-employer-fax" />
                        ) : (
                          <Input className="h-9" placeholder="employer HR email" value={draft.employer_email} onChange={(e) => setD('employer_email', e.target.value)} data-testid="fast-review-employer-email" />
                        )}
                        <Input type="date" className="h-9" value={draft.due_date || ''} onChange={(e) => setD('due_date', e.target.value)} data-testid="fast-review-due-date" />
                      </div>
                    </CheckRow>

                    {current.addendum && (
                      <CheckRow label="Employer's form" chipOk={answersDone}>
                        {unanswered.length === 0 ? (
                          <p className="text-sm text-muted-foreground" data-testid="fast-review-employer-form-complete">
                            Every question is answered — the rest are filled in from the case.
                          </p>
                        ) : (
                          <div className="space-y-2" data-testid="fast-review-employer-form-questions">
                            <p className="text-xs text-muted-foreground">
                              The form asks {unanswered.length === 1 ? 'one thing' : `${unanswered.length} things`} the case can't answer for itself.
                            </p>
                            {drafted.length > 0 && (
                              <Button size="sm" variant="outline" onClick={acceptSuggestions} data-testid="fast-review-accept-suggestions">
                                Accept suggestions ({drafted.length})
                              </Button>
                            )}
                            {groupGaps(unanswered).map((b) => (b.group
                              ? <GapChecklist key={b.group.key} group={b.group} onSet={setGroup} renderItem={renderGap} />
                              : renderGap(b.q)
                            ))}
                          </div>
                        )}
                        {/* ADR 0006 decision 5: the nurse sees the filled pages here,
                            amber where an entry needs a glance, and can fix an answer
                            or move it to the extra sheet before the doctor sees it. */}
                        <div className="mt-3">
                          <FilledFormPreview
                            sid={current.id}
                            onSaved={(r) => setQueue((qs) => qs.map((c) => (c.id === current.id
                              ? { ...c, employer_form_unanswered: Array.isArray(r?.unanswered) ? r.unanswered : c.employer_form_unanswered }
                              : c)))}
                          />
                        </div>
                      </CheckRow>
                    )}

                    <CheckRow label="AI recovery recommendations" chipOk={current.has_ai}>
                      {current.has_ai ? (
                        <p className="text-sm text-muted-foreground" data-testid="fast-review-ai-summary">
                          {current.restriction_count} restriction(s) · return to work ~{current.rtw_weeks || '—'} weeks. Doctor reviews and can edit before signing.
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">Not generated yet — the doctor can generate on review, or run AI prep above.</p>
                      )}
                    </CheckRow>
                  </div>
                )}

                <div className="mt-4 flex items-center justify-between">
                  <Button variant="outline" size="sm" onClick={() => navigate(`/nurse/submission/${current.id}`)} data-testid="fast-review-open-editor-link">
                    <ExternalLink className="h-4 w-4 mr-1.5" /> Open full editor
                  </Button>
                  <span className="text-xs text-muted-foreground tabular-nums">Case {Math.min(idx + 1, queue.length)} of {queue.length}</span>
                </div>
              </CardContent>
            </Card>
        </div>

        {/* ------- Action rail + queue ------- */}
        <div className="space-y-4 lg:sticky lg:top-4">
          <Button
            className="w-full h-14 rounded-xl text-base font-semibold"
            disabled={!canApprove || busy}
            onClick={approve}
            data-testid="fast-review-approve-button"
          >
            {busy ? 'Sending…' : <><Send className="h-5 w-5 mr-1.5" /> Approve & send to doctor</>}
          </Button>
          {!canApprove && draft && blocker && (
            <p className="text-xs text-muted-foreground text-center -mt-2" data-testid="fast-review-blocker">{blocker}</p>
          )}

          <Card className="rounded-2xl">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Up next</CardTitle></CardHeader>
            <CardContent className="pt-0">
              <ScrollArea className="max-h-72">
                <ul className="space-y-1" data-testid="fast-review-queue-list">
                  {queue.map((c, i) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setIdx(i)}
                        data-testid="fast-review-queue-item"
                        className={cn(
                          'w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                          i === idx ? 'border-primary bg-accent text-accent-foreground' : 'border-border bg-card hover:bg-muted'
                        )}
                      >
                        <span className="font-medium truncate flex-1">{c.patient_name || 'Unnamed'}</span>
                        {c.auto_prep?.status === 'done'
                          ? <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
                          : <span className="text-[10px] text-muted-foreground shrink-0">manual</span>}
                        {(c.auto_prep?.flags?.length || 0) > 0 && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-[hsl(32_92%_28%)] shrink-0"><AlertTriangle className="h-3 w-3" />{c.auto_prep.flags.length}</span>
                        )}
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
